/**
 * localStorage layer for the last-selected game mode.
 *
 * Stores a `playerProfile.lastSelected` pointer only — the sealed-serde-session half this module
 * used to own (`readSerde`/`writeSerde`/`quarantineSerde`/`clearAllSerde`, `playerProfile.serde.<M>`)
 * retired with the client-serde-retirement follow-up (2026-07-12): the backend's warehoused corpus
 * has been restore-authoritative since DB slice 2b, and the game-start screen's run-in-progress
 * state has read `GET /api/runs` instead of this module since the same slice — the localStorage copy
 * was write-only dead weight from 2b onward, kept only to feed the server's now-also-retired
 * restore-reconciliation window. `boot.js`'s one-time sweep deletes any `playerProfile.serde.*` keys
 * still lingering from before this PR. On a JSON.parse failure (record corrupted by manual edit,
 * browser bug, version mismatch) or a wrong shape, the key is renamed to `<key>.corrupt-<unix-ms>`
 * rather than deleted — preserves evidence and reads as absent.
 */
const NS = "playerProfile.";
const LAST_SELECTED_KEY = `${NS}lastSelected`;

function renameCorrupt(key) {
  const value = localStorage.getItem(key);
  if (value == null) return;
  try {
    localStorage.setItem(`${key}.corrupt-${Date.now()}`, value);
  } catch {
    // Quota exhaustion while quarantining — drop the rename rather than block the read flow.
  }
  localStorage.removeItem(key);
}

/**
 * Read the last-selected game mode, or null if missing/corrupt. Unknown fields
 * on the stored record are ignored — only `gameMode` is read.
 */
export function readLastSelected() {
  const raw = localStorage.getItem(LAST_SELECTED_KEY);
  if (raw == null) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.gameMode !== "string") {
      renameCorrupt(LAST_SELECTED_KEY);
      return null;
    }
    return parsed;
  } catch {
    renameCorrupt(LAST_SELECTED_KEY);
    return null;
  }
}

/** Write the last-selected game mode — called on every game-start screen mode change and on Play. */
export function writeLastSelected(gameMode) {
  try {
    localStorage.setItem(LAST_SELECTED_KEY, JSON.stringify({ gameMode }));
  } catch (e) {
    console.warn("[player-profile] writeLastSelected failed:", e?.message ?? e);
  }
}
